/**
 * Regional plan copy approvals (LTGC-5711): division plans copied from
 * Approved Regional Plans are approved by per-business-region approvers,
 * distinct from the core-plan copy approvers. Approval documents share the
 * approvalsV2 container with legacy core-copy approvals and are discriminated
 * by this approvalType value; legacy docs never carry the field.
 */
export const REGIONAL_PLAN_COPY_APPROVAL_TYPE = 'regionalPlanCopy'

/**
 * Sentinel sourcePlanId for a from-scratch Community plan approval (LTGC-5756),
 * which has no source Approved Regional Plan to point at. The approval API
 * requires sourcePlanId to be a non-empty string matching
 * /^[A-Za-z0-9*+_-]{1,64}$/, so a reserved value is sent in place of a real id.
 * It is deliberately hyphenated so it cannot collide with a real community plan
 * id, letting the back end recognize and special-case the source-less request.
 */
export const FROM_SCRATCH_SOURCE_PLAN_ID = 'FROM-SCRATCH'

/**
 * Actions an approver can take on a pending request: a decision on a copy
 * approval, or marking a new-elevation request complete once the elevation
 * has been added to the plan.
 */
export enum RegionalPlanApprovalAction {
  APPROVE_NEW_PLAN_NUMBER = 'approveNewPlanNumber',
  APPROVE_SAME_PLAN_NUMBER = 'approveSamePlanNumber',
  COMPLETE = 'complete',
  DECLINE = 'decline',
}

export enum RegionalPlanApprovalStatus {
  APPROVED_NEW_PLAN_NUMBER = 'approvedNewPlanNumber',
  APPROVED_SAME_PLAN_NUMBER = 'approvedSamePlanNumber',
  DECLINED = 'declined',
  PENDING = 'pending',
}

/**
 * Labels for the two decisions an approver picks from the queue row menu. The
 * plan-number outcome is no longer an approver choice — it follows the region
 * automatically — so the menu offers only Approve and Decline. "Decline" mirrors
 * the `Declined` status label shown elsewhere.
 */
export const REGIONAL_PLAN_DECISION_MENU_LABELS = {
  APPROVE: 'Approve',
  COMPLETE: 'Mark as Complete',
  DECLINE: 'Decline',
} as const

/**
 * Row indicator shown to an approver when a request is an out-of-region copy.
 * Approving it forces the requestor onto a new plan number. The short label is
 * the compact chip; the full label explains the consequence in its tooltip,
 * now that the approver no longer selects the plan-number outcome explicitly.
 */
export const REGIONAL_PLAN_OUT_OF_REGION_SHORT_LABEL = 'Out of region'
export const REGIONAL_PLAN_OUT_OF_REGION_LABEL =
  'Out of region — approval requires a new plan number'

/**
 * Shown on a request that reopened an already-approved plan. Its change list is
 * rebased onto the last approved set, so the approver sees only the edit that
 * brought it back to the queue rather than every difference from the source
 * Approved Regional Plan — which they have already approved once.
 */
export const REGIONAL_PLAN_CHANGES_SINCE_APPROVAL_SHORT_LABEL =
  'Since last approval'
export const REGIONAL_PLAN_CHANGES_SINCE_APPROVAL_LABEL =
  'Changed since this plan was last approved — previously approved changes are not repeated'
export const REGIONAL_PLAN_NO_CHANGES_SINCE_APPROVAL_LABEL =
  'No changes since the last approval'

/** Empty state for a first request that differs from the ARP in nothing. */
export const REGIONAL_PLAN_NO_CHANGES_LABEL = 'No attribute changes'

/** Display strings per the LTGC-5711 acceptance criteria (Drafts tab, banners). */
export const REGIONAL_PLAN_APPROVAL_STATUS_LABELS: Record<
  RegionalPlanApprovalStatus,
  string
> = {
  [RegionalPlanApprovalStatus.APPROVED_NEW_PLAN_NUMBER]:
    'Approved with the new plan number',
  [RegionalPlanApprovalStatus.APPROVED_SAME_PLAN_NUMBER]:
    'Approved with the same plan number',
  [RegionalPlanApprovalStatus.DECLINED]: 'Declined',
  [RegionalPlanApprovalStatus.PENDING]: 'Pending',
}

/** The three states of the plan-entry Submit button for ARP-derived drafts. */
export enum RegionalPlanSubmitButtonState {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  SUBMIT = 'SUBMIT',
  SUBMIT_FOR_APPROVAL = 'SUBMIT_FOR_APPROVAL',
}

export const REGIONAL_PLAN_SUBMIT_BUTTON_LABELS: Record<
  RegionalPlanSubmitButtonState,
  string
> = {
  [RegionalPlanSubmitButtonState.PENDING_APPROVAL]: 'Pending approval',
  [RegionalPlanSubmitButtonState.SUBMIT]: 'Submit',
  [RegionalPlanSubmitButtonState.SUBMIT_FOR_APPROVAL]: 'Submit for approval',
}

/** Toast text per decision, announced to the division that requested it. */
export const REGIONAL_PLAN_DECISION_TOAST_MESSAGES: Record<
  Exclude<RegionalPlanApprovalStatus, RegionalPlanApprovalStatus.PENDING>,
  string
> = {
  [RegionalPlanApprovalStatus.APPROVED_NEW_PLAN_NUMBER]:
    'Approved with a new plan number — update the plan number before submitting',
  [RegionalPlanApprovalStatus.APPROVED_SAME_PLAN_NUMBER]:
    'Approved with the same plan number — ready to submit',
  [RegionalPlanApprovalStatus.DECLINED]: 'Regional plan approval was declined',
}

/**
 * Toast copy announced to a regional approver when a new request lands in the
 * region they own. Split into segments so the plan name and the Regional Plan
 * Approvals nav link can be rendered as links between them.
 */
export const REGIONAL_PLAN_REQUEST_TOAST_MESSAGES = {
  LEAD_IN: 'New regional plan approval request for',
  REVIEW_PROMPT: 'Review it on the',
  REVIEW_PROMPT_SUFFIX: 'tab.',
} as const

export const REGIONAL_PLAN_SUBMISSION_MESSAGES = {
  APPROVAL_REQUEST_FAILED: 'Requesting regional plan approval failed',
  APPROVAL_REQUESTED:
    'Approval requested — the plan will appear in Drafts with its approval status',
  OLD_DRAFT_NOT_DELETED:
    'The plan was submitted under the new number, but the original draft could not be deleted — delete it from the Drafts tab',
  PENDING_LOCK:
    'This plan is pending regional approval and cannot be edited or submitted until a decision is made',
  PLAN_NUMBER_CHANGE_FAILED:
    'The plan number could not be changed — nothing was submitted, please try again',
  PLAN_NUMBER_CHANGE_REQUIRED:
    'Approved with a new plan number — you will be asked for the new number when you submit',
  REGION_LAUNCH_STATUS_LOADING:
    'Checking whether this plan requires regional approval…',
  REQUIRED_FIELDS_MISSING:
    'Fill in every required attribute before requesting approval — the missing ones are marked on the form',
  SOURCE_PLAN_LOADING: 'Loading the source regional plan…',
  SUBMITTED_UNDER_NEW_NUMBER: (planNumber: string): string =>
    `Plan submitted as ${planNumber} — the previous draft has been removed`,
} as const

/**
 * Stands in for the plan number of a draft approved only under a NEW number,
 * until the user supplies one at submission. Showing the original number there
 * reads as if it were still usable, which is exactly what the approver refused.
 */
export const REGIONAL_PLAN_NUMBER_PLACEHOLDER =
  '<Enter new number during submission>'

/**
 * Customer segment vocabulary, taken from the values that actually appear on
 * Approved Regional Plan documents (planOverview.customerSegment) in dev02.
 * Extend here if the upstream feed starts sending more.
 */
export const CUSTOMER_SEGMENT_OPTIONS = [
  'First-Time',
  'Move-Up',
  'Move-Down',
  'Luxury',
] as const

/**
 * Synthetic attribute names the diff engine adds alongside real plan
 * attributes when computing the change set that requires approval.
 */
export const REGIONAL_PLAN_CHANGE_ATTRIBUTES = {
  DIVISION: 'division',
  ELEVATION: 'planElevation',
  PLAN_NUMBER: 'planNumber',
} as const

/**
 * User-friendly labels for every attribute the diff engine can emit in a
 * change set — the real plan attributes in COMPARED_ATTRIBUTE_PATHS plus the
 * synthetic REGIONAL_PLAN_CHANGE_ATTRIBUTES. Keeps the approver's Requested
 * Changes column readable instead of showing raw camelCase keys. Any attribute
 * missing here falls back to its raw key at the display site.
 */
export const REGIONAL_PLAN_CHANGE_ATTRIBUTE_LABELS: Record<string, string> = {
  architecturalFirm: 'Architectural Firm',
  bedrooms: 'Bedrooms',
  customerSegment: 'Customer Segment',
  depthOfThePlanInFeet: 'Depth (Feet)',
  depthOfThePlanInInch: 'Depth (Inch)',
  division: 'Division',
  firstFloorCeilingHeightInFeet: '1st Floor Ceiling Height (Feet)',
  firstFloorCeilingHeightInInch: '1st Floor Ceiling Height (Inch)',
  foundationType: 'Foundation Type',
  fullBathrooms: 'Full Bathrooms',
  garageAlignment: 'Garage Alignment',
  garageLoad: 'Garage Load',
  garageType: 'Garage Type',
  halfBathrooms: 'Half Bathrooms',
  homeSqft: 'Home Sqft',
  livingAreas: 'Number of Living Areas',
  marketingName: 'Marketing Name',
  masterBedroomLocation: 'Master Bedroom Location',
  numberOfCarGarages: 'Number Of Car Garages',
  planElevation: 'Elevation',
  planNumber: 'Plan Number',
  planStatus: 'Plan Status',
  planType: 'Plan Type',
  secondFloorCeilingHeightInFeet: '2nd Floor Ceiling Height (Feet)',
  secondFloorCeilingHeightInInch: '2nd Floor Ceiling Height (Inch)',
  stories: 'Stories',
  thirdFloorCeilingHeightInFeet: '3rd Floor Ceiling Height (Feet)',
  thirdFloorCeilingHeightInInch: '3rd Floor Ceiling Height (Inch)',
  widthOfThePlanInFeet: 'Width (Feet)',
  widthOfThePlanInInch: 'Width (Inch)',
  yearOfCreation: 'Year Of Creation',
}

/**
 * A request that a NEW elevation be added to an Approved Regional Plan. Filed
 * from the copy dialog with no draft, no community number and no elevation
 * code — only the requester's reasoning — and routed to the regional approvers
 * of the requester's business region. The lead adds the elevation to the plan
 * and marks the request complete; the plan change feed meanwhile records every
 * elevation the ARP gains so the requester hears about each one. Shares
 * approvalsV2 with copy approvals; told apart by this value.
 */
export const REGIONAL_PLAN_NEW_ELEVATION_APPROVAL_TYPE =
  'regionalPlanNewElevation'

/**
 * Lifecycle of a new-elevation request: pending until the lead marks it
 * complete. `fulfilled` is legacy — written by the plan change feed before
 * completion became the lead's action — and is only ever read.
 */
export enum RegionalPlanNewElevationRequestStatus {
  COMPLETED = 'completed',
  FULFILLED = 'fulfilled',
  PENDING = 'pending',
}

export const REGIONAL_PLAN_NEW_ELEVATION_STATUS_LABELS: Record<
  RegionalPlanNewElevationRequestStatus,
  string
> = {
  [RegionalPlanNewElevationRequestStatus.COMPLETED]: 'Completed',
  [RegionalPlanNewElevationRequestStatus.FULFILLED]: 'Fulfilled',
  // Pending requests deliberately show no status: the row is there so the
  // lead knows the elevation is wanted, until they mark it complete.
  [RegionalPlanNewElevationRequestStatus.PENDING]: '',
}

/**
 * The lines of a new-elevation request in the Requested Changes column: the
 * request itself (naming the code only on a legacy request that carried one),
 * the requester's own words, and — once the plan change feed has seen the plan
 * gain elevations — which ones were added since the request was filed.
 */
export const REGIONAL_PLAN_NEW_ELEVATION_CHANGE_PREFIX =
  'Request for New Elevation'
export const REGIONAL_PLAN_NEW_ELEVATION_REASONING_PREFIX =
  'Reasoning provided by division user'
export const REGIONAL_PLAN_ELEVATIONS_ADDED_PREFIX =
  'Elevations added since request'
export const ELEVATION_LIST_SEPARATOR = ', '

export const NEW_ELEVATION_DESCRIPTION_MAX_LENGTH = 500

/** Diameter of the spinner shown inside the confirm button while sending. */
export const REQUEST_NEW_ELEVATION_PROGRESS_SIZE = 16

export const REQUEST_NEW_ELEVATION_TEXT = {
  BUTTON: 'Request New Elevation',
  CANCEL_BUTTON: 'Cancel',
  CONFIRM_BUTTON: 'Send Request',
  DESCRIPTION: (planNumber: string): string =>
    `Ask the regional lead to add an elevation to Approved Regional Plan ${planNumber}. You will be notified once it can be copied.`,
  DESCRIPTION_LABEL: 'Description',
  REQUEST_FAILED: 'Requesting a new elevation failed',
  REQUEST_SENT: 'New elevation request sent to the regional lead',
  TITLE: 'Request New Elevation',
} as const

/**
 * Toast copy for a new-elevation request. The requester hears each time the
 * plan gains elevations and when the lead marks the request complete; an
 * approver hears when a request arrives in a region they own. Split into
 * segments so the plan can be rendered as a link.
 */
export const REGIONAL_PLAN_NEW_ELEVATION_TOAST_MESSAGES = {
  ELEVATION_ADDED: (elevations: string[]): string =>
    `${elevations.length === 1 ? 'Elevation' : 'Elevations'} ${elevations.join(
      ELEVATION_LIST_SEPARATOR
    )} ${elevations.length === 1 ? 'was' : 'were'} added to`,
  ELEVATION_ADDED_SUFFIX: 'and can now be copied.',
  REQUEST_COMPLETED: 'Your new elevation request was marked complete for',
  REQUEST_COMPLETED_SUFFIX:
    '— the elevation can be copied once the Approved Regional Plan is updated.',
  REQUEST_LEAD_IN: (division: string): string =>
    `${division} requested a new elevation on`,
} as const

/**
 * The upstream decision recorded on an Approved Regional Plan's
 * `approvalStatus`. A consolidated plan decided differently across its
 * divisions arrives with the values joined ('Approved, Specialty').
 */
export const APPROVED_REGIONAL_PLAN_APPROVAL_STATUS = {
  APPROVED: 'Approved',
  APPROVED_WITH_MODS: 'Approved with mods',
  SPECIALTY: 'Specialty',
} as const

/**
 * Why a user cannot copy a given Approved Regional Plan. Copying rolls out by
 * business region (LTGC-5775) on the same allowlist as the from-scratch
 * Community workflow, REACT_APP_LAUNCHED_BUSINESS_REGIONS.
 */
export const REGIONAL_PLAN_COPY_RESTRICTION_TOOLTIPS = {
  APPROVED_WITH_MODS:
    'Approved with mods plans cannot be copied by division users',
  NOT_ENABLED_FOR_REGION: 'Copy is not enabled for your region',
  SPECIALTY_OUTSIDE_DIVISION:
    'Specialty plans can only be copied by users in the plan’s division',
} as const
